import * as React from "react";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Icon from "components/icon/icon";
import { AppContext, AppAction } from "helpers/context";
import { useContext, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./snackBar.module.scss";
import { SnackBarParams } from "helpers/models";

type ActiveSnack = SnackBarParams & {
  key: number;
};

function SnackBar() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();

  // 队列（从 context 读取，不要直接改）
  const queue = context.snackBarParams ?? [];

  const [open, setOpen] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState<ActiveSnack | undefined>(
    undefined
  );

  // 递增 id 生成稳定 key
  const idRef = useRef(0);
  const nextKey = () => ++idRef.current;

  // === 连续消息（Consecutive Snackbar）关键逻辑 ===
  useEffect(() => {
    if (queue.length && !messageInfo) {
      // 1) 没有正在显示的消息：取队头为当前消息并打开
      const first = queue[0];
      setMessageInfo({
        ...first,
        key: nextKey()
      });
      setOpen(true);
      dispatch({ type: AppAction.popSnackBarParams });
    }
  }, [queue, messageInfo, open]);

  // 关闭（不直接出队；出队放在 onExited）
  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // 完全退出：真正从 context 弹出队头，然后清空当前，useEffect 会装载下一条
  const handleExited = () => {
    setMessageInfo(undefined);
  };

  const titleText = messageInfo?.title
    ? t(`snackBar.${messageInfo.title}`, { data: messageInfo.titleData })
    : "";

  const bodyText =
    messageInfo?.resMessage ??
    (messageInfo?.message
      ? t(`snackBar.${messageInfo.message}`, { data: messageInfo.messageData })
      : "");

  // 动作区：自定义按钮 + 关闭按钮；用 useMemo 稳定引用，避免频繁重建导致闪烁
  const actionNode = useMemo(() => {
    let foot: React.ReactNode = null;
    if (typeof messageInfo?.footBtn === "function") {
      // 兼容 footBtn: () => ReactNode
      foot = messageInfo.footBtn();
    } else if (messageInfo?.footBtn) {
      // 兼容 footBtn: ReactNode
      foot = messageInfo.footBtn;
    }
    return (
      <div className={styles.actions}>
        {foot}
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => setOpen(false)}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </div>
    );
  }, [messageInfo?.footBtn]);

  // 注意：使用“当前消息”的稳定 key，避免用会变化的 key 触发卸载重挂
  const snackbarKey = messageInfo?.key;

  return (
    <>
      {messageInfo && (
        <Snackbar
          key={snackbarKey}
          open={open}
          autoHideDuration={messageInfo.autoHideDuration ?? 3000}
          sx={{ zIndex: 1600 }}
          onClose={handleClose}
          TransitionProps={{ onExited: handleExited }}
          message={
            <div
              className={
                messageInfo.footBtn
                  ? styles.footContent
                  : styles.snackBarContent
              }
            >
              <div className="mr-[10px] flex-1 flex flex-col">
                {(messageInfo.titleIcon || titleText) && (
                  <div
                    className={styles.title}
                    style={{ marginBottom: messageInfo.message && "10px" }}
                  >
                    <div
                      className="material-icons"
                      style={{
                        color: messageInfo.titleIconColor || "green",
                        width: "20px",
                        height: "20px",
                        fontSize: "20px",
                        marginRight: "10px"
                      }}
                    >
                      {messageInfo.titleIcon || "check_circle"}
                    </div>
                    {titleText && (
                      <span className="text-[16px]">{titleText}</span>
                    )}
                  </div>
                )}
                {bodyText && <div className={styles.body}>{bodyText}</div>}
              </div>
              {actionNode}
            </div>
          }
        />
      )}
    </>
  );
}

export default SnackBar;
